import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {API_INTG_URL} from "../../apis/api";

const api = axios.create({
    baseURL: API_INTG_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

// Function to set Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };
// Fetch user profile
export const fetchUserProfile = createAsyncThunk(
    'auth/fetchUserProfile',
    async (_, { rejectWithValue }) => {
      try {
        console.log('📌 Fetching user profile...');
        const response = await api.get('/api/auth/profile/');
        console.log('✅ Profile fetched:', response.data);
        return response.data;
      } catch (error) {
        console.error(
          '❌ Error fetching user profile:',
          error.response?.data || error.message
        );
        return rejectWithValue(
          error.response?.data || 'An error occurred while fetching the profile.'
        );
      }
    }
  );
  
  // Update user profile
  export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async (profileData, { rejectWithValue }) => {
      try {
        console.log('📌 Updating user profile...');
        const formData = new FormData();
        for (const key in profileData) {
          if (profileData[key] instanceof File || profileData[key]) {
            formData.append(key, profileData[key]);
          }
        }
        const response = await api.put('/api/auth/profile/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        console.log('✅ Profile updated:', response.data);
        return response.data;
      } catch (error) {
        console.error(
          '❌ Error updating profile:',
          error.response?.data || error.message
        );
        return rejectWithValue(
          error.response?.data || 'An error occurred while updating the profile.'
        );
      }
    }
  );

// Interceptor to handle token expiration
api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.warn("⚠️ Access token expired. Attempting to refresh...");
        try {
          const refreshToken = localStorage.getItem("refresh_token");
          if (!refreshToken) throw new Error("No refresh token available");
          const response = await api.post("/api/auth/token/refresh/", {
            refresh: refreshToken,
          });
          const { access } = response.data;
          localStorage.setItem("access_token", access);
          setAuthToken(access);
          error.config.headers["Authorization"] = `Bearer ${access}`;
          return api.request(error.config);
        } catch (refreshError) {
          console.error("❌ Token refresh failed. Logging out...");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );


// Async thunk for user login
export const loginUser = createAsyncThunk(
    "auth/loginUser",
    async ({ email, password }, { rejectWithValue }) => {
      try {
        const response = await api.post("/api/auth/login/", { email, password });
        const { access, refresh } = response.data.tokens;
  
        localStorage.setItem("access_token", access);
        localStorage.setItem("refresh_token", refresh);
        setAuthToken(access);
        return response.data;
      } catch (error) {
        console.error("Login error:", error); // Log the full error object
        return rejectWithValue(
          error.response?.data?.detail || "An error occurred during login."
        );
      }
    }
  );

// Async thunk for user logout
export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setAuthToken(null);
  });


// Authentication slice
const authSlice = createSlice({
    name: "auth",
    initialState: {
      user: null,
      accessToken: localStorage.getItem("access_token") || null,
      refreshToken: localStorage.getItem("refresh_token") || null,
      loading: false,
      error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(loginUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(loginUser.fulfilled, (state, action) => {
          state.loading = false;
          state.user = action.payload.email;
          state.accessToken = action.payload.tokens.access;
          state.refreshToken = action.payload.tokens.refresh;
        })
        .addCase(loginUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        .addCase(logoutUser.fulfilled, (state) => {
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
        })
        // Handle fetching user profile
        .addCase(fetchUserProfile.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchUserProfile.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload;
        })
        .addCase(fetchUserProfile.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        // Handle updating user profile
        .addCase(updateUserProfile.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateUserProfile.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload;
        })
        .addCase(updateUserProfile.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
  });
  
  export default authSlice.reducer;